import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import ValueText from "../../shared/LabelValueFormatters/ValueText";
import { Grid, Stack, Typography } from "@mui/material";
import { ResultComment } from "../../shared/interfaces/ResultComments";
import { useResultCommentUpdate } from "../../data/useResultCommentUpdate";
import { EditButton } from "../../shared/FormLayout/IconButtons/EditButton";
import { CancelButton } from "../../shared/FormLayout/IconButtons/CancelButton";
import { ConfirmButton } from "../../shared/FormLayout/IconButtons/ConfirmButton";
import { InputTextarea } from "primereact/inputtextarea";
import { AccessControlContext } from "../../contexts/AccessControlContext";

interface Props {
  captureId: number;
  comment: ResultComment;
  onSuccess: (successMessage: string) => void;
  onError: (errorMessage: string) => void;
  customerId?: number;
}

export function ResultCommentUI(props: Props) {
  const { t } = useTranslation();
  const { captureId, comment, onSuccess, onError, customerId } = props;

  const accessControlContext = useContext(AccessControlContext);
  const userCanEditResultComments = accessControlContext.canI("edit", "resultComments", customerId);

  const [editing, setEditing] = useState(false);
  const [editedComment, setEditedComment] = useState(comment.commentString);

  const commentUpdate = useResultCommentUpdate(captureId, {
    onSuccess: handleUpdateCommentSuccess,
    onError: handleUpdateCommentError,
  });

  function handleUpdateCommentSuccess() {
    setEditing(false);
    onSuccess(t("common.commentUpdateSuccess"));
  }

  function handleUpdateCommentError() {
    onError(t("common.dataUpdateError", { val: t("common.comment") }));
  }

  function handleEditButtonClick() {
    setEditing(true);
  }

  function handleCancelButtonClick() {
    setEditing(false);
    setEditedComment(comment.commentString);
  }

  function handleCommentChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    setEditedComment(value);
  }

  function handleSaveEditButtonClick() {
    const updatedComment: Partial<ResultComment> = {
      commentId: comment.commentId,
      commentString: editedComment,
    };
    commentUpdate.mutate(updatedComment);
  }

  return (
    <Grid container>
      <Grid item xs={editing ? 12 : 10} display="flex" alignItems="center">
        <Stack flex={1} alignItems="flex-start">
          <Typography variant="caption" color="textSecondary">
            {t("common.resultsCommentCaption", {
              username: comment.username,
              dateString: comment.commentDateTime,
            })}
          </Typography>
          {editing ? (
            <InputTextarea value={editedComment} onChange={handleCommentChange} autoResize style={{ width: "100%" }} />
          ) : (
            <ValueText>{comment.commentString}</ValueText>
          )}
        </Stack>
      </Grid>
      {userCanEditResultComments && (
        <Grid item xs={editing ? 12 : 2} display="flex" alignItems="center" justifyContent="flex-end">
          {editing ? (
            <>
              <ConfirmButton onClick={handleSaveEditButtonClick} />
              <CancelButton onClick={handleCancelButtonClick} />
            </>
          ) : (
            <EditButton onClick={handleEditButtonClick} />
          )}
        </Grid>
      )}
    </Grid>
  );
}
